import { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudOutlined,
  ContentCopyOutlined,
  MemoryOutlined,
  SlideshowOutlined,
  VideocamOutlined,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import EmptyState from '@/components/common/empty-state';
import { formatDuration, formatMediaRef } from './ops-format';
import type { OpsAttentionRowDto } from '@/types/dtos/ops/ops-platform.dto';

const ATTENTION_PREVIEW = 5;

const SEVERITY_COLOR = {
  critical: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
} as const;

function needsConnectedProcessor(action: OpsAttentionRowDto['primaryAction']['type']): boolean {
  return action === 'retry_failed' || action === 'requeue';
}

function isAdminDeepLink(deepLink?: string): boolean {
  return Boolean(deepLink?.startsWith('/admin'));
}

function DomainIcon({ domain }: { domain: OpsAttentionRowDto['domain'] }) {
  const iconProps = { fontSize: 'small' as const, color: 'action' as const };
  switch (domain) {
    case 'processor':
      return <MemoryOutlined {...iconProps} />;
    case 'singlecam':
      return <VideocamOutlined {...iconProps} />;
    case 'media-agent':
      return <SlideshowOutlined {...iconProps} />;
    case 'vm':
      return <CloudOutlined {...iconProps} />;
  }
}

type OpsAttentionTableProps = {
  rows: OpsAttentionRowDto[];
  loading: boolean;
  processorsAvailable: boolean;
  isActionPending: boolean;
  onRowAction: (row: OpsAttentionRowDto) => void;
  onResetProcessor: (playerId: string) => void;
  onCopyMediaRef: (row: OpsAttentionRowDto) => void;
};

export default function OpsAttentionTable({
  rows,
  loading,
  processorsAvailable,
  isActionPending,
  onRowAction,
  onResetProcessor,
  onCopyMediaRef,
}: OpsAttentionTableProps) {
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);

  const visibleRows = expanded ? rows : rows.slice(0, ATTENTION_PREVIEW);

  const handleCopy = useCallback(
    async (row: OpsAttentionRowDto) => {
      onCopyMediaRef(row);
    },
    [onCopyMediaRef],
  );

  return (
    <Card variant="outlined">
      <CardContent sx={{ 'p': 0, '&:last-child': { pb: 0 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState text={t('admin.opsAdmin.attentionEmpty', undefined, 'No items need attention')} />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 8, p: 0 }} />
                    <TableCell sx={{ width: 36, fontWeight: 600, px: 0.5 }} />
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t('admin.opsAdmin.attentionTable.summary', undefined, 'Summary')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 56 }} align="right">
                      {t('admin.opsAdmin.attentionTable.age', undefined, 'Age')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 200 }} align="right">
                      {t('admin.opsAdmin.attentionTable.action', undefined, 'Action')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.id} hover sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider' } }}>
                      <TableCell sx={{ p: 0, pl: 0.5, verticalAlign: 'middle' }}>
                        <Box
                          sx={{
                            width: 4,
                            height: 28,
                            borderRadius: 0.5,
                            bgcolor: SEVERITY_COLOR[row.severity],
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.5, verticalAlign: 'middle' }}>
                        <Tooltip title={row.domain}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DomainIcon domain={row.domain} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                          {row.summary}
                        </Typography>
                        {row.context.mediaId && row.context.assetVersion != null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', display: 'block' }}
                          >
                            {formatMediaRef(row.context.mediaId, row.context.assetVersion)}
                          </Typography>
                        )}
                        {row.context.errorCode && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {row.context.errorCode}
                          </Typography>
                        )}
                        {row.context.remediationHint && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {row.context.remediationHint}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', verticalAlign: 'middle' }}
                      >
                        {formatDuration(row.ageSeconds)}
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          {(() => {
                            const dispatchBlocked =
                              needsConnectedProcessor(row.primaryAction.type) && !processorsAvailable;
                            const actionButton = (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={isActionPending || dispatchBlocked}
                                onClick={() => onRowAction(row)}
                                sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                              >
                                {row.primaryAction.label}
                              </Button>
                            );
                            return dispatchBlocked ? (
                              <Tooltip
                                title={t(
                                  'admin.opsAdmin.actionNeedsProcessor',
                                  undefined,
                                  'No connected processors — prepare or activate one first',
                                )}
                              >
                                <span>{actionButton}</span>
                              </Tooltip>
                            ) : (
                              actionButton
                            );
                          })()}
                          {row.domain === 'processor' && row.context.playerId && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={isActionPending}
                              onClick={() => onResetProcessor(row.context.playerId!)}
                              sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                            >
                              {t('admin.opsAdmin.attentionTable.reset', undefined, 'Reset')}
                            </Button>
                          )}
                          {row.context.mediaId && row.context.assetVersion != null && (
                            <Tooltip title={t('admin.opsAdmin.copyMediaRef', undefined, 'Copy media ID for support')}>
                              <IconButton
                                size="small"
                                aria-label={t('admin.opsAdmin.copyMediaRef', undefined, 'Copy media reference')}
                                onClick={() => void handleCopy(row)}
                              >
                                <ContentCopyOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdminDeepLink(row.deepLink) && (
                            <Button
                              size="small"
                              component={RouterLink}
                              to={row.deepLink!}
                              sx={{ minWidth: 0, px: 1, fontSize: '0.7rem' }}
                            >
                              {t('admin.opsAdmin.attentionTable.open', undefined, 'Open')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {rows.length > ATTENTION_PREVIEW && (
              <Box sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                <Button size="small" onClick={() => setExpanded((v) => !v)} sx={{ fontSize: '0.75rem' }}>
                  {expanded
                    ? t('admin.opsAdmin.showLess', undefined, 'Show less')
                    : t('admin.opsAdmin.showAllAttention', { count: rows.length }, `Show all (${rows.length})`)}
                </Button>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
